"use client";

import Link from "next/link";
import React from "react";

import { useTranslations } from "next-intl";

import { Feature } from "@/components/ui/Feature";
import { SimpleArchiveModal } from "@/components/ui/Modals/SimpleArchiveModal";
import { FeatureFlags } from "@/enums/feature-flags";
import { useArchiveControl } from "@/hooks/useArchiveControl";
import { usePropertyOptions } from "@/hooks/usePropertyOptions";
import { cn } from "@/utils/cn";

export interface PropertyActionButtonsProps {
  /** Property ID for actions */
  propertyId: string;
  /** CSS class name for custom styling */
  className?: string;
  /** Whether actions are disabled */
  disabled?: boolean;
  /** Edit URL for navigation */
  editUrl?: string;
  /** Property publication status from backend */
  publicationStatus?: string;
  /** Show only essential actions */
  compact?: boolean;
  title?: string;
}

/**
 * PropertyActionButtons - Кнопки действий для риэлторов согласно Figma дизайну
 *
 * Figma Design Specifications (1-5):
 * - Container: 514×54px (кнопки + меню)
 * - Edit Button: 210×54px, #CF1409, border-radius: 15px, белый текст, font-weight: 700
 * - Archive Button: 210×54px, белый фон, #ECECEC border, border-radius: 20px, #373737 текст, font-weight: 500
 * - Context Menu Button: 54×54px, белый фон, #ECECEC border, border-radius: 20px, 3 точки
 * - Gap между кнопками: 20px
 * - Shadow: 5px 5px 25px 15px rgba(235, 235, 235, 0.25)
 */
export const PropertyActionButtons: React.FC<PropertyActionButtonsProps> = ({
  propertyId,
  className,
  disabled = false,
  editUrl,
  publicationStatus,
  compact = false,
  title,
}) => {
  const t = useTranslations("realtor");
  const { getPublicationStatuses } = usePropertyOptions();

  const {
    canArchive,
    canRestore,
    isArchiveModalOpen,
    isRestoreModalOpen,
    openArchiveModal,
    openRestoreModal,
    closeArchiveModal,
    closeRestoreModal,
    handleArchive,
    handleRestore,
    isArchiving,
    isRestoring,
  } = useArchiveControl({
    propertyId: propertyId,
    propertyTitle: title,
  });

  // Get publication statuses from server
  const publicationStatuses = getPublicationStatuses();
  const archivedStatus = publicationStatuses.find((s) => s.key === "archived");
  const publishedStatus = publicationStatuses.find(
    (s) => s.key === "published",
  );

  // Determine if property is archived
  const isArchived = publicationStatus === "archived";

  // Determine archive button text using server data
  const archiveButtonText = isArchived
    ? publishedStatus?.label || t("actionsRestoreText") || "Восстановить"
    : archivedStatus?.label || t("actionsArchiveText") || "В архив";

  return (
    <>
      <div
        className={cn(
          "flex flex-col sm:flex-row items-stretch sm:item-stratch",
          compact ? "gap-2" : "gap-5", // gap-5 = 20px согласно Figma
          {
            "opacity-50 pointer-events-none": disabled,
          },
          className,
        )}
        role="toolbar"
        aria-label={t("actionsToolbarLabel")}
        data-testid="property-action-buttons"
        style={{ position: "relative", zIndex: 10, overflow: "visible" }}
      >
        {/* Красная кнопка "Редактировать" - точные размеры 210×54px */}
        {editUrl ? (
          <Link
            href={editUrl}
            className={cn(
              "inline-flex items-center justify-center",
              "px-[2rem]",
              "min-w-[25rem]",
              "h-[54px]", // Точные размеры из Figma
              "bg-[#CF1409] hover:bg-[#B91308] text-white", // Точный цвет из Figma
              "rounded-[15px]", // Точный border-radius из Figma
              "font-bold text-[16px] leading-[20px]", // Figma typography: font-weight: 700
              "transition-colors duration-200",
              "shadow-[5px_5px_25px_15px_rgba(235,235,235,0.25)]", // Точная тень из Figma
              {
                "pointer-events-none opacity-50": disabled,
                "w-auto px-4": compact,
              },
            )}
            data-testid="edit-button"
          >
            {t("actionsEditText")}
          </Link>
        ) : (
          <button
            type="button"
            className={cn(
              "inline-flex items-center justify-center",
              "h-[54px]",
              "px-[2rem]",
              "min-w-[25rem]",
              "bg-[#CF1409] hover:bg-[#B91308] text-white",
              "rounded-[15px]",
              "font-bold text-[16px] leading-[20px]",
              "transition-colors duration-200",
              "shadow-[5px_5px_25px_15px_rgba(235,235,235,0.25)]",
              "font-[family-name:Geometria]",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              {
                "w-auto px-4": compact,
              },
            )}
            onClick={() => {}}
            disabled={disabled}
            data-testid="edit-button"
          >
            {t("actionsEditText")}
          </button>
        )}

        <Feature name={FeatureFlags.PROPERTY_ARCHIVE_ACTION}>
          <Feature.Enabled>
            {/* Белая кнопка "Отправить в архив" - точные размеры 210×54px */}
            <button
              type="button"
              className={cn(
                "inline-flex items-center justify-center",
                "h-[54px]", // Точные размеры из Figma
                "px-[2rem]",
                "min-w-[25rem]",
                "bg-white hover:bg-gray-50 text-[#373737]", // Точные цвета из Figma
                "border border-[#ECECEC]", // Точный цвет бордера из Figma
                "rounded-[20px]", // Другой border-radius для archive кнопки (20px vs 15px)
                "font-medium text-[16px] leading-[20px]", // font-weight: 500 из Figma
                "transition-colors duration-200",
                "shadow-[5px_5px_25px_15px_rgba(235,235,235,0.25)]", // Та же тень
                "disabled:opacity-50 disabled:cursor-not-allowed",
                {
                  "w-auto px-4": compact,
                },
              )}
              onClick={isArchived ? openRestoreModal : openArchiveModal}
              disabled={disabled}
              data-testid="archive-button"
            >
              {archiveButtonText}
            </button>
          </Feature.Enabled>
        </Feature>
        {/* Новое рабочее контекстное меню */}
        {/* Відключено на першу інтерацію кабінета рієлтора */}
        {/* <SimpleContextMenu
        propertyId={propertyId}
        disabled={disabled}
        hideEdit={!!editUrl} // Hide edit action if main edit button exists
        publicationStatus={publicationStatus}
        className="flex-shrink-0"
      /> */}
      </div>
      <SimpleArchiveModal
        isOpen={isArchiveModalOpen}
        onClose={closeArchiveModal}
        onArchive={handleArchive}
        mode="archive"
        propertyTitle={title}
        isLoading={isArchiving}
      />
      <SimpleArchiveModal
        isOpen={isRestoreModalOpen}
        onClose={closeRestoreModal}
        onArchive={handleRestore}
        mode="restore"
        propertyTitle={title}
        isLoading={isRestoring}
      />
    </>
  );
};

export default PropertyActionButtons;
